import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, MapPin, Mail, ToggleLeft, ToggleRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import BottomNav from "@/components/BottomNav";
import { getSessionToken } from "@/utils/auth";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function EstimateFormSettings() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addressStepEnabled, setAddressStepEnabled] = useState(true);
  const [collectEmailEnabled, setCollectEmailEnabled] = useState(false);
  const [funnelV2Enabled, setFunnelV2Enabled] = useState(false);
  const [funnelV3Enabled, setFunnelV3Enabled] = useState(false);

  useEffect(() => {
    fetchUser();
    fetchSettings();
  }, []);

  const fetchUser = async () => {
    try {
      const token = getSessionToken();
      if (!token) {
        navigate("/login");
        return;
      }
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      navigate("/login");
    }
  };

  const fetchSettings = async () => {
    try {
      const token = getSessionToken();
      const [addressRes, emailRes, funnelRes, funnelV3Res] = await Promise.all([
        axios.get(`${API}/settings/address-step`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/settings/collect-email`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/settings/estimate-funnel-v2`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/settings/estimate-funnel-v3`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setAddressStepEnabled(addressRes.data.enabled);
      setCollectEmailEnabled(emailRes.data.enabled);
      setFunnelV2Enabled(funnelRes.data.enabled);
      setFunnelV3Enabled(funnelV3Res.data.enabled);
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddressToggle = async () => {
    setSaving(true);
    const newValue = !addressStepEnabled;
    
    try {
      const token = getSessionToken();
      await axios.put(`${API}/settings/address-step`, 
        { enabled: newValue },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setAddressStepEnabled(newValue);
      toast.success(newValue ? "Address step enabled" : "Address step disabled");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleEmailToggle = async () => {
    setSaving(true);
    const newValue = !collectEmailEnabled;
    
    try {
      const token = getSessionToken();
      await axios.put(`${API}/settings/collect-email`, 
        { enabled: newValue },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setCollectEmailEnabled(newValue);
      toast.success(newValue ? "Email collection enabled" : "Email collection disabled");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleFunnelV2Toggle = async () => {
    setSaving(true);
    const newValue = !funnelV2Enabled;

    try {
      const token = getSessionToken();
      await axios.put(`${API}/settings/estimate-funnel-v2`,
        { enabled: newValue },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setFunnelV2Enabled(newValue);
      // Server auto-disables V3 when V2 is turned on (mutual exclusion).
      if (newValue) setFunnelV3Enabled(false);
      toast.success(newValue ? "V2 funnel is now live" : "Reverted to original funnel (V1)");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleFunnelV3Toggle = async () => {
    setSaving(true);
    const newValue = !funnelV3Enabled;

    try {
      const token = getSessionToken();
      await axios.put(`${API}/settings/estimate-funnel-v3`,
        { enabled: newValue },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setFunnelV3Enabled(newValue);
      // Server auto-disables V2 when V3 is turned on (mutual exclusion).
      if (newValue) setFunnelV2Enabled(false);
      toast.success(newValue ? "V3 funnel (Whole-Home Screen Check) is now live" : "Reverted to original funnel (V1)");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-zinc-200 border-t-[#207024]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 pb-20">
      <Header user={user} />
      
      <div className="max-w-md mx-auto p-4">
        {/* Back button */}
        <button
          onClick={() => navigate('/settings')}
          className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Settings</span>
        </button>

        <div className="mb-6">
          <h1 className="font-barlow text-2xl font-bold text-zinc-900 uppercase">Estimate Form</h1>
          <p className="text-zinc-600 mt-1">Configure the instant estimate flow</p>
        </div>

        <div className="space-y-4">
          {/* Funnel V2 - Simplified Condition Picker */}
          <div className="bg-white rounded-xl border p-5" data-testid="funnel-v2-card">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-6 h-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-zinc-900">New Funnel (V2)</h3>
                  <button
                    onClick={handleFunnelV2Toggle}
                    disabled={saving}
                    className="focus:outline-none"
                    data-testid="funnel-v2-toggle"
                  >
                    {funnelV2Enabled ? (
                      <ToggleRight className="w-10 h-10 text-[#207024]" />
                    ) : (
                      <ToggleLeft className="w-10 h-10 text-zinc-300" />
                    )}
                  </button>
                </div>
                <p className="text-sm text-zinc-500 mt-1">
                  Simplified condition picker (Rescreen / New / Not Sure), removes the Pet Grill upsell, and refines PetScreen + Disposal copy. Toggle off to revert to the original funnel.
                </p>
                <div className={`mt-3 text-xs font-medium px-2 py-1 rounded inline-block ${
                  funnelV2Enabled
                    ? "bg-green-100 text-green-700"
                    : "bg-zinc-100 text-zinc-600"
                }`}>
                  {funnelV2Enabled ? "V2 funnel live" : "Original funnel live"}
                </div>
              </div>
            </div>

            {funnelV2Enabled && (
              <div className="mt-4 pt-4 border-t border-zinc-100 text-sm text-zinc-600 space-y-2">
                <p><strong>What changes:</strong></p>
                <ul className="list-disc list-inside space-y-1 text-zinc-500">
                  <li>3 simple options instead of split inputs</li>
                  <li>"Not Sure" auto-splits 50/50 (rounds up new)</li>
                  <li>Pet Grill upsell removed; PetScreen + Disposal copy refreshed</li>
                </ul>
              </div>
            )}
          </div>

          {/* Funnel V3 — Whole-Home Screen Check (mutually exclusive with V2) */}
          <div className="bg-white rounded-xl border p-5" data-testid="funnel-v3-card">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-6 h-6 text-emerald-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-zinc-900">New Funnel (V3) — Whole-Home Screen Check</h3>
                  <button
                    onClick={handleFunnelV3Toggle}
                    disabled={saving}
                    className="focus:outline-none"
                    data-testid="funnel-v3-toggle"
                  >
                    {funnelV3Enabled ? (
                      <ToggleRight className="w-10 h-10 text-[#207024]" />
                    ) : (
                      <ToggleLeft className="w-10 h-10 text-zinc-300" />
                    )}
                  </button>
                </div>
                <p className="text-sm text-zinc-500 mt-1">
                  Right before booking, asks the customer if we can check the rest of their screens while we're on-site.
                  Aims to lift average screens per completed job. Turning this on automatically turns V2 off — only
                  one version is live at a time, and historical analytics are preserved for every version.
                </p>
                <div className={`mt-3 text-xs font-medium px-2 py-1 rounded inline-block ${
                  funnelV3Enabled
                    ? "bg-green-100 text-green-700"
                    : "bg-zinc-100 text-zinc-600"
                }`}>
                  {funnelV3Enabled ? "V3 funnel live" : (funnelV2Enabled ? "V2 funnel live" : "Original funnel live")}
                </div>
              </div>
            </div>

            {funnelV3Enabled && (
              <div className="mt-4 pt-4 border-t border-zinc-100 text-sm text-zinc-600 space-y-2">
                <p><strong>What changes:</strong></p>
                <ul className="list-disc list-inside space-y-1 text-zinc-500">
                  <li>New "While we're there…" question after the estimate</li>
                  <li>3 options: No thanks / Quick look / Whole-home check</li>
                  <li>Answer is shown on the tech's job card so they know before arriving</li>
                  <li>Screen Check breakdown appears in Funnel Analytics</li>
                </ul>
              </div>
            )}
          </div>

          {/* Email Collection Setting */}
          <div className="bg-white rounded-xl border p-5">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-zinc-900">Collect Email</h3>
                  <button
                    onClick={handleEmailToggle}
                    disabled={saving}
                    className="focus:outline-none"
                    data-testid="collect-email-toggle"
                  >
                    {collectEmailEnabled ? (
                      <ToggleRight className="w-10 h-10 text-[#207024]" />
                    ) : (
                      <ToggleLeft className="w-10 h-10 text-zinc-300" />
                    )}
                  </button>
                </div>
                <p className="text-sm text-zinc-500 mt-1">
                  Show an optional email field on the contact step. Useful for receipts and confirmations.
                </p>
                <div className={`mt-3 text-xs font-medium px-2 py-1 rounded inline-block ${
                  collectEmailEnabled 
                    ? "bg-green-100 text-green-700" 
                    : "bg-zinc-100 text-zinc-600"
                }`}>
                  {collectEmailEnabled ? "Showing email field" : "Email field hidden"}
                </div>
              </div>
            </div>
          </div>

          {/* Address Step Setting */}
          <div className="bg-white rounded-xl border p-5">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6 text-teal-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-zinc-900">Address Step Before Estimate</h3>
                  <button
                    onClick={handleAddressToggle}
                    disabled={saving}
                    className="focus:outline-none"
                    data-testid="address-step-toggle"
                  >
                    {addressStepEnabled ? (
                      <ToggleRight className="w-10 h-10 text-[#207024]" />
                    ) : (
                      <ToggleLeft className="w-10 h-10 text-zinc-300" />
                    )}
                  </button>
                </div>
                <p className="text-sm text-zinc-500 mt-1">
                  Ask for the job location before showing the estimate to confirm service area.
                </p>
                <div className={`mt-3 text-xs font-medium px-2 py-1 rounded inline-block ${
                  addressStepEnabled 
                    ? "bg-green-100 text-green-700" 
                    : "bg-zinc-100 text-zinc-600"
                }`}>
                  {addressStepEnabled ? "Address step enabled" : "Skipping address step"}
                </div>
              </div>
            </div>

            {/* Additional info when enabled */}
            {addressStepEnabled && (
              <div className="mt-4 pt-4 border-t border-zinc-100 text-sm text-zinc-600 space-y-2">
                <p><strong>What happens:</strong></p>
                <ul className="list-disc list-inside space-y-1 text-zinc-500">
                  <li>Address step appears after contact info</li>
                  <li>Uses Google Maps autocomplete</li>
                  <li>Users can skip if they prefer</li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      <BottomNav user={user} />
    </div>
  );
}
